import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DashCard } from "../../UI/DashCard";
import { formatEventTotalDisplay } from "../../../utils/eventTotalsSaUtils";
import { calcRolls } from "../../../utils/printLabelStatsUtils";
import { getPomot, setPomot } from "../../../utils/pomotLocalStorage";
import { useCompanyPrintLabelStatsContext } from "./CompanyPrintLabelStatsContext";

export function CompanyConsumablesCalculator({ companyId }) {
  const {
    loading,
    error,
    periodHint,
    companyWideLabels: labels,
  } = useCompanyPrintLabelStatsContext();

  const labelKeys = useMemo(
    () => labels.map((l) => l.key).join("\0"),
    [labels],
  );

  const [pomotByKey, setPomotByKey] = useState({});

  useEffect(() => {
    if (!companyId) {
      setPomotByKey({});
      return;
    }
    const keys = labelKeys ? labelKeys.split("\0") : [];
    const next = {};
    for (const key of keys) {
      next[key] = getPomot(companyId, key);
    }
    setPomotByKey((prev) => {
      const prevKeys = Object.keys(prev);
      if (
        prevKeys.length === keys.length &&
        keys.every((key) => prev[key] === next[key])
      ) {
        return prev;
      }
      return next;
    });
  }, [companyId, labelKeys]);

  const handlePomotChange = useCallback(
    (labelKey, rawValue) => {
      if (!companyId) {
        return;
      }
      const value = Math.max(0, Number(rawValue) || 0);
      setPomot(companyId, labelKey, value);
      setPomotByKey((prev) => ({ ...prev, [labelKey]: value }));
    },
    [companyId],
  );

  return (
    <DashCard
      className="dash-card-consumables-calculator"
      title="Калькулятор расходников"
    >
      <p className="event-totals-period-hint consumables-calculator-period-hint">
        {periodHint}
      </p>
      {loading ? (
        <div
          className="event-totals-skeleton consumables-calculator-skeleton"
          aria-busy="true"
          aria-label="Загрузка калькулятора"
        >
          <div className="event-totals-skeleton-chip" />
          <div className="event-totals-skeleton-chip" />
          <div className="event-totals-skeleton-chip" />
        </div>
      ) : error ? (
        <p className="event-totals-muted">Не удалось загрузить данные печати</p>
      ) : labels.length === 0 ? (
        <p className="event-totals-muted">Нет печати за период</p>
      ) : (
        <div className="consumables-calculator-scroll">
          <table className="consumables-calculator-table">
            <tbody>
              <tr>
                <th className="consumables-calculator-row-label" scope="row">
                  размер
                </th>
                {labels.map((row) => (
                  <td key={row.key}>{row.display}</td>
                ))}
              </tr>
              <tr>
                <th className="consumables-calculator-row-label" scope="row">
                  кол-во
                </th>
                {labels.map((row) => (
                  <td key={row.key}>{formatEventTotalDisplay(row.count)}</td>
                ))}
              </tr>
              <tr>
                <th className="consumables-calculator-row-label" scope="row">
                  помот
                </th>
                {labels.map((row) => (
                  <td key={row.key}>
                    <input
                      type="number"
                      className="consumables-calculator-input"
                      min={0}
                      step={1}
                      value={pomotByKey[row.key] ?? 0}
                      aria-label={`Помот, ${row.display}`}
                      onChange={(e) =>
                        handlePomotChange(row.key, e.target.value)
                      }
                    />
                  </td>
                ))}
              </tr>
              <tr>
                <th className="consumables-calculator-row-label" scope="row">
                  ролики
                </th>
                {labels.map((row) => {
                  const pomot = pomotByKey[row.key] ?? 0;
                  const rolls = calcRolls(row.count, pomot);
                  return (
                    <td key={row.key}>
                      {rolls == null ? "—" : formatEventTotalDisplay(rolls)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </DashCard>
  );
}
